import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { ChangeLog, Pagination, Response, SelectItemProps } from '@ui/src';
import { ProcessingRule, PspIntegration, PspPartner } from '../types';
import { processingRulesApi } from './service';
import ROUTES from '../../../../routes';

export interface ProcessingRulesState {
  processingRules?: ProcessingRule[];
  pagination?: Pagination;
  processingRule?: ProcessingRule;
  takeCount: number;
  pspPartnes?: SelectItemProps[];
  pspIntegrations?: SelectItemProps[];
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
}

const initialState: ProcessingRulesState = {
  takeCount: 0,
};

export const processingRulesSlice = createSlice({
  name: 'pocessingRules',
  initialState,
  reducers: {
    setTakeCount: (state, action: PayloadAction<number>) => {
      state.takeCount = action.payload;
    },
    clearProcessingRulesState: (state) => {
      state.processingRules = undefined;
      state.pagination = undefined;
      state.takeCount = 0;
    },
    clearProcessingRuleState: (state) => {
      state.processingRule = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      processingRulesApi.endpoints.getProcessingRules.matchFulfilled,
      (state, action: PayloadAction<Response<ProcessingRule>>) => {
        state.processingRules = action.payload.data?.edges?.map((item) => ({
          ...item.node,
          url: generatePath(ROUTES.TRANSACTION_SETTINGS_PROCESSING_RULES_EDIT, {
            ruleId: item.node.id,
          }),
        }));

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      processingRulesApi.endpoints.getProcessingRulesPspPartners.matchFulfilled,
      (state, action: PayloadAction<Response<PspPartner>>) => {
        state.pspPartnes = action?.payload?.data?.edges?.map((item) => ({
          name: item.node.companyName,
          value: item.node.id,
        }));
      },
    );
    builder.addMatcher(
      processingRulesApi.endpoints.getProcessingRulesPspIntegrations
        .matchFulfilled,
      (state, action: PayloadAction<Response<PspIntegration>>) => {
        state.pspIntegrations = action?.payload?.data?.edges?.map((item) => ({
          name: item.node.name,
          value: item.node.id,
        }));
      },
    );
    builder.addMatcher(
      processingRulesApi.endpoints.updateProcessingRule.matchFulfilled,
      (state, action: PayloadAction<{ data: ProcessingRule }>) => {
        state.processingRules = state.processingRules?.map((item) => {
          let result = { ...item };

          if (result.id === action?.payload?.data?.id) {
            result = {
              ...action.payload.data,
              url: generatePath(
                ROUTES.TRANSACTION_SETTINGS_PROCESSING_RULES_EDIT,
                { ruleId: item.id },
              ),
            };
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      processingRulesApi.endpoints.getProcessingRule.matchFulfilled,
      (state, action: PayloadAction<{ data: ProcessingRule }>) => {
        state.processingRule = action.payload.data;
      },
    );
    builder.addMatcher(
      processingRulesApi.endpoints.getProcessingRulesChangeLogs.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
  },
});

export const {
  clearProcessingRuleState,
  clearProcessingRulesState,
  setTakeCount,
} = processingRulesSlice.actions;

export default processingRulesSlice.reducer;
